import React, { useReducer, useEffect } from "react";
import Picker from "../components/SizePicker.js";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as Actions from "../actions/Actions.js";
import { useNavigate } from "react-router-dom";
import AuthenticationHOC from "../components/AuthenticationHOC.js";
import queryString from "query-string";
import { useLocation } from "react-router-dom";
import Helper from "../functions/Helper.js";
import PromiseButton from "../components/PromiseButton";
import { HashLink as Link } from "react-router-hash-link";
import Spinner from "react-bootstrap/Spinner";
import Slider from "@mui/material/Slider";

import { styled } from "@mui/system";
import { Helmet } from "react-helmet";

const StyledSlider = styled(Slider)({
  color: "var(--primary-color-600)",
  "& .MuiSlider-thumb": {
    marginTop: -9,
    width: 5,
    borderRadius: 0,
    height: 30,
    marginLeft: 0,
    color: "#aaa",
    "&:focus, &:hover, &.Mui-active": {
      boxShadow: "none",
    },
  },
  "& .MuiSlider-track": {
    height: 12,
  },
  "& .MuiSlider-rail": {
    height: 12,
  },
  "& .MuiSlider-valueLabel": {
    left: "calc(-50% + -12px)",
    top: -22,
    "& *": {
      background: "transparent",
      color: "#000",
    },
  },
});
const verify = (jwt) => {
  return fetch(__server__ + "/API/user/verify", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + jwt,
    },
  })
    .then((response) => response.json())
    .then((data) => {
      return data?.success === "verified";
    });
};

const calculateRating = ({ cushion, traction, fit, materials, support }) => {
  if (
    traction != -1 &&
    cushion !== -1 &&
    fit !== -1 &&
    materials !== -1 &&
    support !== -1
  ) {
    return (traction + cushion + fit + materials + support) / 5;
  } else {
    return -1;
  }
};

const submitReview = (state, jwt, shoe_id, dispatch) => {
  return (async () => {
    let response = await fetch(__server__ + "/API/user/review", {
      method: state.edit ? "PUT" : "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + jwt,
      },
      body: JSON.stringify({
        ...state,
        published: 0,
        durability: state.durability ?? -1,
        outdoor: state.outdoor ?? -1,
        style_id: state.style_id ?? -1,
        shoe_id: shoe_id,
        rating: state.rating,
      }),
    });
    if (response.ok) {
      let data = await response.json();
      if (data.success === "success") {
        dispatch({ type: "STATUS", value: "SUCCESS" });
      } else {
        dispatch({ type: "STATUS", value: "ERROR", errorMsg: data?.msg });
      }
    } else {
      dispatch({
        type: "STATUS",
        value: "ERROR",
        errorMsg: "There was an error saving the review",
      });
    }
  })();
};

const checkValid = (state) => {
  return (
    state.traction !== -1 &&
    state.cushion !== -1 &&
    state.fit !== -1 &&
    state.materials !== -1 &&
    state.support !== -1 &&
    state.size !== -1 &&
    state.width !== -1 &&
    (state.summary.length === 0 || state.summary.length > 50)
  );
};

const rating_values = [
  { label: "10 amazing", rating: 10 },
  { label: "9 great", rating: 9 },
  { label: "8 good", rating: 8 },
  { label: "7 average", rating: 7 },
  { label: "6 subpar", rating: 6 },
  { label: "5 bad", rating: 5 },
];

const size_values = [
  { label: "very small", rating: 0 },
  { label: "slightly small", rating: 25 },
  { label: "true to size", rating: 50 },
  { label: "slightly long", rating: 75 },
  { label: "very long", rating: 100 },
];

const width_values = [
  { label: "very narrow", rating: 0 },
  { label: "slightly narrow", rating: 25 },
  { label: "regular", rating: 50 },
  { label: "slightly wide", rating: 75 },
  { label: "very wide", rating: 100 },
];

const outdoor_values = [
  { label: "NA", rating: -1 },
  { label: "bad", rating: 0 },
  { label: "fair", rating: 50 },
  { label: "good", rating: 100 },
];

const categories = [
  {
    category: "traction",
    optional: false,
    values: rating_values,
    description:
      "How do the shoes grip the floor on clean surfaces? How does the traction pattern handle dust?",
    getColor: (x) => Helper.get_color(x, "bg"),
  },
  {
    category: "cushion",
    optional: false,
    values: rating_values,
    description:
      "Does the cushioning offer the impact protection, bounce, and comfort you expect from basketball shoes?",
    getColor: (x) => Helper.get_color(x, "bg"),
  },
  {
    category: "support",
    optional: false,
    values: rating_values,
    description:
      "Do your feel securely locked-in and stable making quick cuts or did you experience heel slippage or side-to-side movement on the footbed?",
    getColor: (x) => Helper.get_color(x, "bg"),
  },
  {
    category: "materials",
    optional: false,
    values: rating_values,
    description:
      "Are the materials comfortable, soft and breathable? Or do they feel cheap in-hand and take forever to break in?",
    getColor: (x) => Helper.get_color(x, "bg"),
  },
  {
    category: "fit",
    optional: false,
    values: rating_values,
    description: "Do the shoes fit like a glove or did you get blisters?",
    getColor: (x) => Helper.get_color(x, "bg"),
  },
  {
    category: "durability",
    optional: true,
    values: [{ label: "NA", rating: -1 }, ...rating_values],
    description:
      "(Optional) How is the shoe holding up after extended playing time?",
    getColor: (x) => Helper.get_color(x, "bg"),
    getCellWidth: (x) => (x.rating === -1 ? 60 : 110),
  },
  {
    category: "outdoor",
    optional: true,
    values: outdoor_values,
    description: "(Optional) How well do the shoes hold up playing outdoors?",
    getColor: (x) => Helper.get_color(x, "outdoor"),
  },
  {
    category: "size",
    optional: true,
    values: size_values,
    description: "Did you go true to size or did you have to size up or down?",
    getColor: () => "#666",
    getCellWidth: () => 144,
  },
  {
    category: "width",
    optional: true,
    values: width_values,
    description:
      "Is the fit of the shoe narrow, regular or did you experience some dead space?",
    getColor: () => "#666",
    getCellWidth: () => 144,
  },
];

const initialState = {
  traction: -1,
  cushion: -1,
  fit: -1,
  materials: -1,
  support: -1,
  size: -1,
  width: -1,
  outdoor: null,
  durability: null,
  summary: "",
  weight_size: -1,
  showDialog: false,
  styles: [],
  showStyleModal: false,
  style_id: null,
  shoe: false,
  edit: false,
  status: "",
  weight_unit: "grams",
  weight_value: null,
  edit_weight: false,
  errorMsg: "",
  rating: -1,
};

function reducer(state, action) {
  switch (action.type) {
    case "CHANGE_RATING":
      return {
        ...state,
        [action.value.category]: action.value.rating,
        rating:
          state.rating === -1
            ? calculateRating({
                ...state,
                [action.value.category]: action.value.rating,
              })
            : state.rating,
      };
    case "CHANGE_SUMMARY":
      return {
        ...state,
        summary: action.value.slice(0, 500),
      };
    case "SHOW_DIALOG":
      return {
        ...state,
        showDialog: action.value,
      };
    case "CANCEL_DIALOG":
      return {
        ...state,
        showDialog: false,
        weight_size: -1,
        weight_value: null,
      };
    case "STYLE_MODAL":
      return {
        ...state,
        showStyleModal: action.value,
      };
    case "STYLE_ID":
      return {
        ...state,
        style_id: action.value,
      };
    case "SHOE":
      return {
        ...state,
        shoe: action.value,
      };
    case "LOAD":
      return {
        ...state,
        ...action.value,
        edit: true,
      };
    case "STATUS":
      return {
        ...state,
        status: action.value,
        errorMsg: action.errorMsg,
      };
    case "SLIDER":
      return {
        ...state,
        rating: action.value,
      };
    default:
      throw new Error();
  }
}

function UserRating(props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    verify(props.jwt).then((x) => {
      if (!x) {
        navigate("/shoe-reviews/login", {
          state: { nextPath: location.pathname + location.search },
        });
      }
    });
  }, []);

  useEffect(() => {
    (async () => {
      let queryParams = queryString.parse(location.search);

      if (queryParams.shoe_id) {
        let response = await fetch(
          __server__ + "/API/pub/shoe_data?shoe_id=" + queryParams.shoe_id,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        let data = await response.json();

        if (queryParams.user_review_id) {
          let userReviewResponse = await fetch(
            __server__ + "/API/pub/user_review/" + queryParams.user_review_id,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          let userReviewData = await userReviewResponse.json();

          dispatch({ type: "SHOE", value: data });
          dispatch({ type: "LOAD", value: userReviewData });
        } else {
          dispatch({ type: "SHOE", value: data });
        }
      }
    })();
  }, []);

  let pickedStyle =
    state.shoe && state.shoe.styles.find((x) => x.style_id == state.style_id);

  if (state.shoe) {
    return (
      <div style={{ maxWidth: 720, paddingBottom: 400 }}>
        <Helmet>
          <title>Your Rating of {state.shoe.shoe_name}</title>
        </Helmet>
        <Modal
          size="xl"
          show={state.showStyleModal}
          onHide={() => dispatch({ type: "STYLE_MODAL", value: false })}
        >
          <Modal.Header closeButton>
            <Modal.Title>Choose Your Colorway:</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              <Row style={{ margin: "0px -5px" }}>
                {state.shoe.styles.map((style) => {
                  return (
                    <Col
                      style={{ padding: "15px 5px" }}
                      key={style.style_id}
                      xs={6}
                      sm={4}
                      md={3}
                      lg={2}
                    >
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          paddingBottom: "80%",
                        }}
                      >
                        <img
                          alt="Chosen Style"
                          onClick={() =>
                            dispatch({
                              type: "STYLE_ID",
                              value: style.style_id,
                            })
                          }
                          style={{
                            position: "absolute",
                            padding: "10px 5px",
                            objectFit: "contain",
                            objectPosition: "0% 80%",
                            transform: style.mirror == "1" ? "scaleX(-1)" : "",
                            backgroundColor: "#f9f9f9",
                            cursor: "pointer",
                            width: "100%",
                            height: "100%",
                            border:
                              state.style_id === style.style_id
                                ? "3px solid #2780e3"
                                : "1px solid #cccccc",
                          }}
                          src={Helper.add_options_img(style.picture_url, 286)}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          marginTop: 5,
                          lineHeight: "17px",
                        }}
                      >
                        {style.name}
                      </div>
                    </Col>
                  );
                })}
                <Col
                  style={{ padding: "15px 5px" }}
                  xs={6}
                  sm={4}
                  md={3}
                  lg={2}
                >
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      paddingBottom: "80%",
                    }}
                  >
                    <div
                      alt="Chosen Style"
                      onClick={() => dispatch({ type: "STYLE_ID", value: -1 })}
                      style={{
                        position: "absolute",
                        padding: "10px 5px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f9f9f9",
                        cursor: "pointer",
                        width: "100%",
                        height: "100%",
                        border:
                          state.style_id === -1
                            ? "3px solid #2780e3"
                            : "1px solid #cccccc",
                      }}
                    >
                      <i
                        style={{ fontSize: 60, color: "#ccc" }}
                        className="far fa-question-square"
                      />
                    </div>
                  </div>
                  <div
                    style={{ fontSize: 14, marginTop: 5, lineHeight: "17px" }}
                  >
                    Other Colorway
                  </div>
                </Col>
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <PromiseButton
              onClick={() => dispatch({ type: "STYLE_MODAL", value: false })}
              variant="primary"
            >
              Save
            </PromiseButton>
          </Modal.Footer>
        </Modal>

        <h1 style={{ marginBottom: 40 }} className="thg_h2_size">
          Your Rating of the {state.shoe.shoe_name}
        </h1>
        <Alert style={{ fontSize: 14, marginTop: 10 }} variant="success">
          Please only review shoes you have played-in yourself. Give a fair and
          honest review for every category. Even if you really love a shoe,
          don't just give it a 10 in each category if it doesn't deserve it.
        </Alert>

        <div style={{ marginBottom: 40 }}>
          <div style={{ marginBottom: 5, fontWeight: 600 }}>Your Colorway:</div>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 10 }}>
            (Optional) Pick the colorway of the {state.shoe.shoe_name} you are
            reviewing.
          </div>
          {!state.style_id && (
            <PromiseButton
              size="sm"
              variant="primary"
              onClick={() => dispatch({ type: "STYLE_MODAL", value: true })}
              style={{}}
            >
              Choose Colorway
            </PromiseButton>
          )}
          {state.style_id && (
            <div
              style={{ cursor: "pointer" }}
              onClick={() => dispatch({ type: "STYLE_MODAL", value: true })}
            >
              {state.style_id === -1 ? (
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 16,
                    color: "var(--primary-color-700)",
                    cursor: "pointer",
                  }}
                >
                  Other Colorway
                </div>
              ) : (
                <>
                  <img
                    alt="Chosen Style"
                    style={{
                      padding: "10px 5px",
                      transform: pickedStyle.mirror == "1" ? "scaleX(-1)" : "",
                      cursor: "pointer",
                    }}
                    src={Helper.add_options_img(pickedStyle.picture_url, 143)}
                  />

                  <div
                    style={{ fontSize: 14, marginTop: 5, lineHeight: "17px" }}
                  >
                    {pickedStyle.name}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {categories.map((category, index) => {
          let addProps = {};
          if (category.hasOwnProperty("getCellWidth")) {
            addProps.getCellWidth = category.getCellWidth;
          }
          return (
            <div key={index} style={{ marginBottom: 60 }}>
              <div
                id={category.category}
                style={{ marginBottom: 5, fontWeight: 600 }}
              >
                {Helper.capitalize(category.category)}:
              </div>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 5 }}>
                {category.description}
              </div>
              <Picker
                {...addProps}
                getColor={category.getColor}
                values={category.values}
                changeRating={(x) => {
                  dispatch({
                    type: "CHANGE_RATING",
                    value: { category: category.category, rating: x },
                  });
                }}
                activeRating={state[category.category]}
              />
            </div>
          );
        })}

        <div style={{}}>
          <div style={{ marginBottom: 15 }}>
            <div style={{ marginBottom: 5, fontWeight: 600, fontSize: 20 }}>
              Overall Rating:
            </div>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 5 }}>
              Calculated by taking the average of traction, cushion, materials,
              fit and support. You can also assign your own number.
            </div>
            <div
              style={{
                fontSize: 60,
                marginTop: 20,
                lineHeight: "60px",
                fontWeight: 600,
                color: Helper.get_color(state.rating, "text"),
              }}
            >
              {state.rating === -1 ? "NA" : state.rating.toFixed(1)}
            </div>
            <div style={{ maxWidth: 400 }}>
              <StyledSlider
                value={state.rating}
                min={5}
                max={10}
                step={0.1}
                valueLabelFormat={(x) => x}
                onChange={(event, value) => {
                  dispatch({ type: "SLIDER", value: value });
                }}
                onChangeCommitted={() => {}}
                valueLabelDisplay="off"
                aria-labelledby="range-slider"
              />
            </div>
          </div>

          <div style={{ marginBottom: 5, fontWeight: 600, fontSize: 20 }}>
            Your Review:
          </div>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 10 }}>
            (Optional) What did you like, what did you hate about this shoe?
            What kind of player should wear this shoe? Detailed reviews will be
            shown at the top.
          </div>
          <Form.Control
            value={state.summary}
            onChange={(x) => {
              dispatch({ type: "CHANGE_SUMMARY", value: x.target.value });
            }}
            as="textarea"
            placeholder=""
            style={{ fontSize: 16, height: "200px" }}
          />
          <div
            style={{
              color: state.summary.length === 500 ? "red" : "inherit",
              textAlign: "right",
              fontSize: 14,
              marginTop: 5,
            }}
          >
            {state.summary.length} / 500
          </div>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 5 }}>
            I reserve the right to fix grammar or spelling mistakes before
            publishing any user reviews.
          </div>
        </div>
        {!checkValid(state) && (
          <Alert
            style={{ whiteSpace: "pre-wrap", fontSize: 14, marginTop: 10 }}
            variant="danger"
          >
            {categories
              .filter((x) => !x.optional)
              .map(
                (x) =>
                  state[x.category] === -1 && (
                    <div key={x.category}>
                      Assign a value for {x.category}.{" "}
                      <Link
                        style={{ color: "inherit", fontWeight: 600 }}
                        to={"#" + x.category}
                      >
                        (Click here to fix)
                      </Link>
                    </div>
                  )
              )}
            {state.size === -1 && (
              <div>
                Assign a value for size.{" "}
                <Link
                  style={{ color: "inherit", fontWeight: 600 }}
                  to={"#size"}
                >
                  (Click here to fix)
                </Link>
              </div>
            )}
            {state.width === -1 && (
              <div>
                Assign a value for width.{" "}
                <Link
                  style={{ color: "inherit", fontWeight: 600 }}
                  to={"#width"}
                >
                  (Click here to fix)
                </Link>
              </div>
            )}
            {state.summary.length > 0 && state.summary.length < 50 && (
              <div>Your review should be at least 50 characters long.</div>
            )}
          </Alert>
        )}

        <PromiseButton
          style={{ display: state.status !== "SUCCESS" ? "block" : "none" }}
          onClick={() =>
            submitReview(state, props.jwt, state.shoe.shoe_id, dispatch)
          }
          disabled={!checkValid(state)}
          className="mr-2"
          variant="primary"
        >
          Save Review
        </PromiseButton>

        {state.status === "ERROR" && (
          <Alert style={{ fontSize: 14, marginTop: 10 }} variant="danger">
            {state.errorMsg}
          </Alert>
        )}

        {state.status === "SUCCESS" && (
          <Alert style={{ fontSize: 14, marginTop: 10 }} variant="success">
            Successfully saved review. Reviews take up to 24h to be published
            but meanwhile you can see your own reviews in your{" "}
            <Link to={"/shoe-reviews/account"}>account</Link>.
          </Alert>
        )}
      </div>
    );
  } else {
    return (
      <div>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }
}

export default connect(
  (state) => ({
    ...state.persistData,
    currentShoe: state.data.currentShoe,
    data: state.data.data,
  }),
  (dispatch) => ({
    actions: bindActionCreators(Actions, dispatch),
  })
)(AuthenticationHOC(UserRating));
